import http from '@/utils/http'
import { BaseService } from '../common/base.service'
import type {
  UserInfo,
  RegisterRequest,
  RegisterResponse,
  ChangePasswordRequest,
  UpdateProfileRequest
} from './models'

/**
 * 用户服务类
 * 处理用户相关的API请求
 */
export class UserService extends BaseService {
  protected readonly baseUrl = '/api/account'

  /**
   * 获取当前用户详细信息
   * @returns 用户信息
   */
  async getCurrentUser(): Promise<UserInfo> {
    try {
      this.log('获取当前用户信息')
      
      const response = await this.get<UserInfo>('my-profile')
      
      this.log('用户信息获取成功', { userName: response?.userName })
      return response
    } catch (error) {
      this.logError('获取用户信息失败', error)
      throw error
    }
  }

  /**
   * 更新当前用户资料
   * @param data 更新数据
   * @returns 更新后的用户信息
   */
  async updateProfile(data: UpdateProfileRequest): Promise<UserInfo> {
    try {
      this.log('更新用户资料', { name: data.name })

      const response = await this.put<UserInfo>('my-profile', data)

      this.log('用户资料更新成功')
      return response
    } catch (error) {
      this.logError('更新用户资料失败', error)
      throw error
    }
  }

  /**
   * 修改密码
   * @param data 修改密码数据
   */
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    try {
      this.log('修改密码')
      
      await this.post('my-profile/change-password', data)
      
      this.log('密码修改成功')
    } catch (error) {
      this.logError('密码修改失败', error)
      throw error
    }
  }

  /**
   * 用户注册
   * @param data 注册数据
   * @returns 注册响应
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    try {
      this.log('用户注册', { userName: data.userName })
      
      const response = await this.post<RegisterResponse>('register', {
        userName: data.userName,
        emailAddress: data.emailAddress,
        password: data.password,
        appName: data.appName || 'Owl'
      })
      
      this.log('用户注册成功', { id: response.id })
      return response
    } catch (error) {
      this.logError('用户注册失败', error)
      throw error
    }
  }

  /**
   * 获取用户列表（管理员功能）
   * @param params 查询参数
   * @returns 用户列表
   */
  async getUsers(params?: {
    skipCount?: number
    maxResultCount?: number
    sorting?: string
    filter?: string
  }): Promise<{ items: UserInfo[], totalCount: number }> {
    try {
      this.log('获取用户列表', params)
      
      // 使用http直接请求，避免BaseService添加baseUrl前缀
      const response = await http.get<{ items: UserInfo[], totalCount: number }>('/api/identity/users', { params })
      
      this.log('用户列表获取成功', { count: response.data.totalCount })
      return response.data
    } catch (error) {
      this.logError('获取用户列表失败', error)
      return { items: [], totalCount: 0 }
    }
  }

  /**
   * 根据ID获取用户信息
   * @param id 用户ID
   * @returns 用户信息
   */
  async getUserById(id: string): Promise<UserInfo> {
    try {
      this.log('获取用户信息', { id })

      const response = await this.get<UserInfo>(`/api/identity/users/${id}`)

      this.log('用户信息获取成功', { userName: response?.userName })
      return response
    } catch (error) {
      this.logError('获取用户信息失败', error)
      throw error
    }
  }
}

// 创建单例实例
export const userService = new UserService()